import { useCallback, useEffect, useState } from 'react';
import { api, type MovieRating } from '../api/client.js';

/**
 * Ratings for a set of films.
 *
 * Fetched in one request rather than one per row: the history and the results
 * screen both show a whole list at once, and a request per card would turn a
 * list into a thundering herd.
 *
 * The server's answer is authoritative — it recomputes the average after
 * every change and hands it straight back — so there is no optimistic update
 * to get wrong here.
 */
export function useRatings(movieIds: string[]) {
  const [ratings, setRatings] = useState<Record<string, MovieRating>>({});
  const [loading, setLoading] = useState(false);

  // The array is rebuilt on every render, so the effect keys off its contents.
  const key = movieIds.join(',');

  const load = useCallback(async () => {
    if (!key) {
      setRatings({});
      return;
    }
    setLoading(true);
    try {
      const result = await api.ratings(key.split(','));
      setRatings(Object.fromEntries(result.ratings.map((rating) => [rating.movieId, rating])));
    } catch {
      // A missing rating is a blank line, not an error worth showing.
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = (rating: MovieRating) =>
    setRatings((current) => ({ ...current, [rating.movieId]: rating }));

  return {
    ratings,
    loading,
    rate: async (movieId: string, score: number) => {
      try {
        apply((await api.rate(movieId, score)).rating);
      } catch {
        void load();
      }
    },
    clear: async (movieId: string) => {
      try {
        apply((await api.clearRating(movieId)).rating);
      } catch {
        void load();
      }
    },
  };
}
